import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getErrorMessage, isApiError } from "@/app/http";
import { useNowMinuteTick } from "@/app/useNowMinuteTick";
import { useNowSecondTick } from "@/app/useNowSecondTick";
import { useToast } from "@/components/feedback/useToast";
import { Button } from "@/components/ui/button";
import { listFamilies, listFamilyKids, queueAdjustTime, queueSetTime } from "./api";
import { KidDeviceCard } from "./components/KidDeviceCard";
import { screenBudgetQueryKeys } from "./queryKeys";
import type { FamilySummary, KidStatusSummary, KidViewState, QueueCommandInput } from "./types";
import { useScreenBudgetEvents } from "./useScreenBudgetEvents";

interface ScreenBudgetPageProps {
  onUnauthorized: () => void;
  isAdmin?: boolean;
}

interface QueueMutationContext {
  queryKey: ReturnType<typeof screenBudgetQueryKeys.kids>;
  previousKids?: KidStatusSummary[];
  previousViewState?: KidViewState;
}

function emptyKidViewState(): KidViewState {
  return {
    optimisticDeltaSeconds: 0,
    isQueueing: false,
    lastQueuedAt: null,
    lastQueueError: null,
    flashToken: 0,
  };
}

export function ScreenBudgetPage({ onUnauthorized, isAdmin = false }: ScreenBudgetPageProps) {
  const nowMinuteMs = useNowMinuteTick();
  const nowSecondMs = useNowSecondTick();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [selectedFamilyId, setSelectedFamilyId] = useState<number | null>(null);
  const [kidViewById, setKidViewById] = useState<Record<number, KidViewState>>({});
  const [isPageHidden, setIsPageHidden] = useState(() => document.hidden);

  const kidQueueChainRef = useRef<Map<number, Promise<unknown>>>(new Map());
  const kidViewByIdRef = useRef<Record<number, KidViewState>>({});

  useEffect(() => {
    kidViewByIdRef.current = kidViewById;
  }, [kidViewById]);

  useEffect(() => {
    const onVisibilityChange = () => {
      setIsPageHidden(document.hidden);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const familiesQuery = useQuery({
    queryKey: screenBudgetQueryKeys.families,
    queryFn: async (): Promise<FamilySummary[]> => {
      const data = await listFamilies();
      return data.families;
    },
  });

  const kidsQuery = useQuery({
    queryKey: screenBudgetQueryKeys.kids(selectedFamilyId),
    enabled: selectedFamilyId !== null,
    queryFn: async (): Promise<KidStatusSummary[]> => {
      if (selectedFamilyId === null) {
        return [];
      }
      const data = await listFamilyKids(selectedFamilyId);
      return data.kids;
    },
  });

  const sendCommandMutation = useMutation<
    { message: unknown },
    unknown,
    QueueCommandInput,
    QueueMutationContext
  >({
    mutationFn: async command => {
      if (command.type === "adjust") {
        return queueAdjustTime(command.kidId, { deltaSeconds: command.value });
      }
      return queueSetTime(command.kidId, { targetSeconds: Math.max(0, Math.trunc(command.value)) });
    },
    async onMutate(command) {
      const queryKey = screenBudgetQueryKeys.kids(command.familyId);
      await queryClient.cancelQueries({ queryKey });

      const previousKids = queryClient.getQueryData<KidStatusSummary[]>(queryKey);
      const previousViewState = kidViewByIdRef.current[command.kidId];

      let optimisticDelta = command.type === "adjust" ? command.value : 0;

      queryClient.setQueryData<KidStatusSummary[]>(queryKey, current => {
        if (!current) return current;

        return current.map(kid => {
          if (kid.kidId !== command.kidId) return kid;

          const nextEffective =
            command.type === "adjust"
              ? Math.max(0, kid.effectiveTimeLeftSeconds + command.value)
              : Math.max(0, Math.trunc(command.value));

          optimisticDelta = nextEffective - kid.effectiveTimeLeftSeconds;

          return {
            ...kid,
            effectiveTimeLeftSeconds: nextEffective,
            outstandingMessageCount: kid.outstandingMessageCount + 1,
          };
        });
      });

      const nowIso = new Date().toISOString();
      setKidViewById(current => {
        const prevState = current[command.kidId] ?? emptyKidViewState();
        return {
          ...current,
          [command.kidId]: {
            optimisticDeltaSeconds: prevState.optimisticDeltaSeconds + optimisticDelta,
            isQueueing: true,
            lastQueuedAt: nowIso,
            lastQueueError: null,
            flashToken: prevState.flashToken + 1,
          },
        };
      });

      showToast({ title: `Queued ${command.label} for ${command.kidName}`, variant: "success" });

      return {
        queryKey,
        previousKids,
        previousViewState,
      };
    },
    onError(error, command, context) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }

      if (context?.previousKids) {
        queryClient.setQueryData(context.queryKey, context.previousKids);
      }

      const message = getErrorMessage(error, "Failed to queue command");
      setKidViewById(current => {
        const fallback = emptyKidViewState();
        const restored = context?.previousViewState ?? current[command.kidId] ?? fallback;
        return {
          ...current,
          [command.kidId]: {
            ...restored,
            isQueueing: false,
            lastQueueError: message,
            flashToken: restored.flashToken + 1,
          },
        };
      });

      showToast({ title: message, variant: "error" });
    },
    onSettled(_data, _error, command, context) {
      const queryKey = context?.queryKey ?? screenBudgetQueryKeys.kids(command.familyId);
      void queryClient.invalidateQueries({ queryKey }).finally(() => {
        setKidViewById(current => {
          const prevState = current[command.kidId] ?? emptyKidViewState();
          return {
            ...current,
            [command.kidId]: {
              ...prevState,
              isQueueing: false,
            },
          };
        });
      });
    },
  });

  useEffect(() => {
    const families = familiesQuery.data ?? [];

    if (families.length === 0) {
      if (selectedFamilyId !== null) {
        setSelectedFamilyId(null);
      }
      return;
    }

    if (selectedFamilyId === null || !families.some(family => family.id === selectedFamilyId)) {
      setSelectedFamilyId(families[0]?.id ?? null);
    }
  }, [familiesQuery.data, selectedFamilyId]);

  useEffect(() => {
    if (isApiError(familiesQuery.error) && familiesQuery.error.status === 401) {
      onUnauthorized();
    }
  }, [familiesQuery.error, onUnauthorized]);

  useEffect(() => {
    if (isApiError(kidsQuery.error) && kidsQuery.error.status === 401) {
      onUnauthorized();
    }
  }, [kidsQuery.error, onUnauthorized]);

  const { isStreamHealthy } = useScreenBudgetEvents({
    familyId: selectedFamilyId,
    enabled: selectedFamilyId !== null,
    onUnauthorized,
  });

  useEffect(() => {
    if (selectedFamilyId === null) return;
    if (isStreamHealthy) return;

    const intervalMs = isPageHidden ? 60_000 : 15_000;
    const interval = window.setInterval(() => {
      void queryClient.invalidateQueries({
        queryKey: screenBudgetQueryKeys.kids(selectedFamilyId),
      });
    }, intervalMs);

    return () => {
      window.clearInterval(interval);
    };
  }, [isPageHidden, isStreamHealthy, queryClient, selectedFamilyId]);

  useEffect(() => {
    if (!kidsQuery.data) return;

    setKidViewById(current => {
      const next: Record<number, KidViewState> = {};

      for (const kid of kidsQuery.data) {
        const previous = current[kid.kidId] ?? emptyKidViewState();
        next[kid.kidId] = {
          ...previous,
          optimisticDeltaSeconds: 0,
          isQueueing: false,
        };
      }

      return next;
    });
  }, [kidsQuery.data]);

  const generalError = useMemo(() => {
    const errors = [familiesQuery.error, kidsQuery.error];

    for (const error of errors) {
      if (error && !(isApiError(error) && error.status === 401)) {
        return getErrorMessage(error, "Request failed");
      }
    }

    return null;
  }, [familiesQuery.error, kidsQuery.error]);

  const families = familiesQuery.data ?? [];
  const kids = kidsQuery.data ?? [];
  const showFamilySelector = families.length > 1;

  const queueCommand = (input: Omit<QueueCommandInput, "familyId">) => {
    if (selectedFamilyId === null) return;

    const command: QueueCommandInput = {
      ...input,
      familyId: selectedFamilyId,
    };

    const previous = kidQueueChainRef.current.get(command.kidId) ?? Promise.resolve();
    const run = previous
      .catch(() => undefined)
      .then(() => sendCommandMutation.mutateAsync(command));

    const tracked = run
      .catch(() => undefined)
      .finally(() => {
        const current = kidQueueChainRef.current.get(command.kidId);
        if (current === tracked) {
          kidQueueChainRef.current.delete(command.kidId);
        }
      });

    kidQueueChainRef.current.set(command.kidId, tracked);
  };

  return (
    <div className="grid gap-5">
      <section className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Screen Budget</h1>
          <p className="text-sm text-muted-foreground">Quick daily controls for family devices.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showFamilySelector ? (
            <select
              id="screen-budget-active-family"
              className="h-9 min-w-[190px] rounded-md border border-input bg-background px-2 text-sm"
              value={selectedFamilyId ?? ""}
              onChange={event => {
                const value = Number.parseInt(event.currentTarget.value, 10);
                setSelectedFamilyId(Number.isFinite(value) ? value : null);
              }}
              aria-label="Active family"
            >
              {families.map(family => (
                <option key={family.id} value={family.id}>
                  {family.name}
                </option>
              ))}
            </select>
          ) : null}
          {isAdmin ? (
            <Button asChild size="sm" variant="outline">
              <Link to="/apps/screen-budget/admin">Admin</Link>
            </Button>
          ) : null}
        </div>
      </section>

      <section className="grid gap-3" aria-live="polite">
        {generalError ? <p className="text-sm text-red-600">{generalError}</p> : null}
        {familiesQuery.isPending ? (
          <p className="text-sm text-muted-foreground">Loading families...</p>
        ) : families.length === 0 ? (
          <p className="text-sm text-muted-foreground">No family is available for this account. Contact an admin.</p>
        ) : kidsQuery.isPending ? (
          <p className="text-sm text-muted-foreground">Loading devices...</p>
        ) : kids.length === 0 ? (
          <p className="text-sm text-muted-foreground">No paired devices yet.</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {kids.map(kid => {
              const uiState = kidViewById[kid.kidId] ?? emptyKidViewState();
              return (
                <KidDeviceCard
                  key={kid.kidId}
                  kid={kid}
                  uiState={uiState}
                  nowMinuteMs={nowMinuteMs}
                  nowSecondMs={nowSecondMs}
                  onQueueAdjust={(seconds, label) =>
                    queueCommand({
                      kidId: kid.kidId,
                      kidName: kid.displayName,
                      type: "adjust",
                      value: seconds,
                      label,
                    })
                  }
                  onQueueSet={(seconds, label) =>
                    queueCommand({
                      kidId: kid.kidId,
                      kidName: kid.displayName,
                      type: "set",
                      value: seconds,
                      label,
                    })
                  }
                />
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
